import React, { useEffect, useRef, useState } from 'react';
import { companyAiService, type CompanyKnowledgeDocumentSummary } from '@/services/companyAiService';
import { checkKnowledgeProvisions, type ProvisionMatch } from '@/services/certificateKnowledgeService';
import './CertificateProvisionCheck.css';

// Do not present prose accidentally extracted by the generic PDF parser as an edition.
export function referenceEdition(value: string | null) {
  return value && /[0-9]/.test(value) ? value : 'Edition not verified - check source';
}

export default function CertificateProvisionCheck({ onSelect }: { onSelect: (text: string) => void }) {
  const [documents, setDocuments] = useState<CompanyKnowledgeDocumentSummary[]>([]);
  const [documentId, setDocumentId] = useState('');
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<ProvisionMatch[]>([]);
  const [selected, setSelected] = useState<ProvisionMatch | null>(null);
  const [draft, setDraft] = useState('');
  const [message, setMessage] = useState('Loading indexed NCC references…');
  const [busy, setBusy] = useState(false);
  const requestId = useRef(0);
  useEffect(() => {
    let active = true;
    companyAiService.listKnowledgeDocuments({ query: 'NCC' }).then(rows => {
      if (!active) return;
      setDocuments(rows);
      setMessage(rows.length ? 'Select the reference edition, then search a clause number or phrase.' : 'No indexed NCC references. Import and index the reference PDFs in Company Knowledge first.');
    }).catch(() => { if (active) setMessage('References could not be loaded. You can still enter provisions manually.'); });
    return () => { active = false; requestId.current++; };
  }, []);
  const clear = () => { requestId.current++; setMatches([]); setSelected(null); setDraft(''); setBusy(false); setMessage(''); };
  const check = async () => {
    const id = ++requestId.current;
    setSelected(null); setMatches([]); setBusy(true); setMessage('Searching indexed reference text…');
    try {
      const rows = await checkKnowledgeProvisions(Number(documentId), query.trim());
      if (id !== requestId.current) return;
      setMatches(rows);
      setMessage(rows.length ? 'Review the original reference text before applying. Up to 20 matches are shown.' : 'No matching indexed text. Try a clause number or a shorter phrase.');
    } catch { if (id === requestId.current) setMessage('Reference search failed. Please try again.'); }
    finally { if (id === requestId.current) setBusy(false); }
  };
  return <div className="certificate-provision-check">
    <label>Reference document / edition<select value={documentId} onChange={e => { clear(); setDocumentId(e.target.value); }}>
      <option value="">Select an indexed NCC reference</option>
      {documents.map(d => <option key={d.knowledgeDocumentId} value={d.knowledgeDocumentId}>{d.title} · {referenceEdition(d.versionText)}</option>)}
    </select></label>
    <label>Clause number or phrase<input type="search" maxLength={200} value={query} placeholder="e.g. H1P1 or structural reliability" onChange={e => { clear(); setQuery(e.target.value); }} onKeyDown={e => { if(e.key === 'Enter') { e.preventDefault(); if(documentId && query.trim().length >= 2 && !busy) void check(); } }} /></label>
    <button type="button" disabled={!documentId || query.trim().length < 2 || busy} onClick={() => void check()}>{busy ? 'Checking…' : 'Check provision'}</button>
    <p role="status">{message}</p>
    <ul aria-label="Reference matches">{matches.map(m => <li key={m.blockId}><button type="button" aria-pressed={selected?.blockId === m.blockId} onClick={() => { setSelected(m); setDraft(m.text); }}>
      <strong>{m.heading || m.title}</strong><span>PDF page {m.pageNumber ?? 'not recorded'}</span><span className="certificate-provision-check__excerpt">{m.text}</span>
    </button></li>)}</ul>
    {selected && <div className="certificate-provision-check__review">
      <strong>{selected.title} · {referenceEdition(selected.version)}</strong>
      <span>PDF page {selected.pageNumber ?? 'not recorded'} · {selected.heading}</span>
      <span className="certificate-provision-check__source">Source: {selected.sourcePath || 'Company knowledge library'}</span>
      <details><summary>Original indexed text</summary><p style={{whiteSpace:'pre-wrap'}}>{selected.text}</p></details>
      <label>Provision to apply<textarea rows={6} value={draft} onChange={e => setDraft(e.target.value)} /></label>
      <small>Review applicability and shorten the text as needed. Apply adds it to this certificate; save the certificate to retain it.</small>
      <button type="button" disabled={!draft.trim()} onClick={() => {
        const citation = [selected.title, referenceEdition(selected.version), selected.heading, selected.pageNumber ? `PDF p.${selected.pageNumber}` : null].filter(Boolean).join(' · ');
        onSelect(`${citation}\n${draft.trim()}`); setSelected(null); setDraft(''); setMessage('Provision added. Save the certificate to retain it.');
      }}>Apply</button>
    </div>}
  </div>;
}
